import { useCallback } from "react"
import { IProjectFilter } from "../../application/models"
import { IProject } from "../../application/models/IProject"
import { IProjectReport } from "../../application/models/IProjectReport"
import { IProjectResume } from "../../application/states/project"
import { useAxiosInstances } from "../axios-instance"
import { IInferenceRequest, IInferenceResponse } from "../../application/models/IInference"

export const useProjectApi = () => {
  const { axiosAuthInstance } = useAxiosInstances()

  const findUserProjects = useCallback((data: {
    userId: string
    page?: number
    limit?: number
    active?: boolean
    filters?: {
      name?: string
      createdBy?: string
      startDate?: Date
      endDate?: Date
    }
  }): Promise<{
    projects: IProjectResume[]
    total: number
    page: number
    userList: { id: string, name: string, email: string }[]
  }> => {

    return axiosAuthInstance.get('/routes/project/user-projects', {
      params: {
        userId: data.userId,
        page: data.page,
        limit: data.limit,
        active: data.active,
        name: data.filters?.name,
        createdBy: data.filters?.createdBy,
        startDate: data.filters?.startDate,
        endDate: data.filters?.endDate
      }
    }).then((response) => {
      const projects = response.data?.result?.projectList
      const page = response.data?.result?.page
      const total = response.data?.result?.total
      const userList = response.data?.result?.userList
      if (projects) {
        return {
          projects: projects,
          page: page,
          total: total,
          userList: userList ?? []
        }
      } else {
        throw {
          type: 'NotFoundError',
          message: 'Projetos não encontrados'
        }
      }
    }).catch((error) => {
      if (error?.response?.data?.result) {
        throw {
          message: error.response.data.result.message,
          type: error.response.data.result.type
        }
      } else {
        throw {
          message: 'Erro desconhecido',
          type: 'unknown'
        }
      }
    })
  }, [axiosAuthInstance])

  const findProjectById = useCallback((data: {
    projectId: string
  }): Promise<IProject> => {
    return axiosAuthInstance.get('/routes/project/' + data.projectId).then((response) => {
      const project = response.data?.result?.project
      if (project) {
        return project
      } else {
        throw {
          type: 'NotFoundError',
          message: 'Projeto não encontrado'
        }
      }
    }).catch((error) => {
      if (error?.response?.data?.result) {
        throw {
          message: error.response.data.result.message,
          type: error.response.data.result.type
        }
      } else {
        throw {
          message: 'Erro desconhecido',
          type: 'unknown'
        }
      }
    })
  }, [axiosAuthInstance])

  const updateFilter = useCallback(async (data: {
    filter: IProjectFilter,
    projectId: string
  }): Promise<{
    projectFilter: IProjectFilter,
    projectReport: IProjectReport
  }> => {
    try {
      const response = await axiosAuthInstance.put('/routes/project/update-filter', {
        projectFilter: data.filter,
        projectId: data.projectId
      }, {
        timeout: 300000
      })

      const projectFilter = response.data.result.projectFilter
      const projectReport = response.data.result.projectReport

      return {
        projectFilter: projectFilter,
        projectReport: projectReport
      }

    } catch (error) {
      throw error
    }
  }, [axiosAuthInstance])

  const exportProjectApi = useCallback(async (data: {
    filterId?: string,
    list?: string[],
    name: string,
    companyId: string,
    createdBy: string,
    projectId: string,
    total: number,
    geolocation?: {
      lat: number
      lon: number
      radius: number
    }
  }): Promise<void> => {
    return axiosAuthInstance.post('/routes/project-export', {
      projectData: {
        filterId: data.filterId ?? undefined,
        name: data.name,
        companyId: data.companyId,
        createdBy: data.createdBy,
        projectId: data.projectId,
        total: data.total,
        list: data.list ?? undefined,
        geolocation: data.geolocation ?? undefined
      }
    })
  }, [axiosAuthInstance])

  const createProjectApi = useCallback((data: {
    name: string,
    companyId: string,
    createdBy: string
  }): Promise<IProject> => {
    return axiosAuthInstance.post('/routes/project', {
      projectData: {
        name: data.name,
        companyId: data.companyId,
        createdBy: data.createdBy
      }
    }).then((response) => {
      const project = response.data?.result?.project
      if (project) {
        return project
      } else {
        throw {
          type: 'NotFoundError',
          message: 'Projeto não encontrado'
        }
      }
    }).catch((error) => {
      if (error?.response?.data?.result) {
        throw {
          message: error.response.data.result.message,
          type: error.response.data.result.type
        }
      } else {
        throw {
          message: 'Erro desconhecido',
          type: 'unknown'
        }
      }
    })
  }, [axiosAuthInstance])

  const projectIsExportingApi = useCallback((data: {
    projectId: string
  }): Promise<boolean> => {
    return axiosAuthInstance.get('/routes/project/is-exporting', {
      params: {
        projectId: data.projectId
      }
    }).then((response) => {
      const isExporting = response.data?.result?.isExporting
      if (isExporting) {
        return isExporting
      } else {
        return false
      }
    }).catch((error) => {
      if (error?.response?.data?.result) {
        throw {
          message: error.response.data.result.message,
          type: error.response.data.result.type
        }
      } else {
        throw {
          message: 'Erro desconhecido',
          type: 'unknown'
        }
      }
    })
  }, [axiosAuthInstance])

  const updateProjectStatusApi = useCallback((data: {
    projectId: string,
    status: boolean
  }): Promise<void> => {
    return axiosAuthInstance.put('/routes/project/status', {
      projectId: data.projectId,
      status: data.status
    }).then((response) => {

      const project = response.data?.result?.project
      if (project) {
        return project
      } else {
        throw {
          type: 'NotFoundError',
          message: 'Projeto não encontrado'
        }
      }
    }).catch((error) => {
      if (error?.response?.data?.result) {
        throw {
          message: error.response.data.result.message,
          type: error.response.data.result.type
        }
      } else {
        throw {
          message: 'Erro desconhecido',
          type: 'unknown'
        }
      }
    })
  }, [axiosAuthInstance])


  const updateProjectNameApi = useCallback((data: {
    projectId: string,
    name: string
  }): Promise<void> => {
    return axiosAuthInstance.put('/routes/project/name', {
      projectId: data.projectId,
      name: data.name
    }).then((response) => {
      const project = response.data?.result?.project
      if (project) {
        return project
      } else {
        throw {
          type: 'NotFoundError',
          message: 'Projeto não encontrado'
        }
      }
    }).catch((error) => {
      if (error?.response?.data?.result) {
        throw {
          message: error.response.data.result.message,
          type: error.response.data.result.type
        }
      } else {
        throw {
          message: 'Erro desconhecido',
          type: 'unknown'
        }
      }
    })
  }, [axiosAuthInstance])

  const cloneProjectApi = useCallback((data: {
    projectId: string,
    createdBy: string,
    name: string
  }): Promise<IProject> => {
    return axiosAuthInstance.post('/routes/project/clone', {
      projectId: data.projectId,
      createdBy: data.createdBy,
      name: data.name
    }).then((response) => {
      const project = response.data?.result?.project
      if (project) {
        return project
      } else {
        throw {
          type: 'NotFoundError',
          message: 'Projeto não encontrado'
        }
      }
    }).catch((error) => {
      if (error?.response?.data?.result) {
        throw {
          message: error.response.data.result.message,
          type: error.response.data.result.type
        }
      } else {
        throw {
          message: 'Erro desconhecido',
          type: 'unknown'
        }
      }
    })
  }, [axiosAuthInstance])

  const fetchInference = useCallback(
    async (data: Partial<IInferenceRequest>): Promise<IInferenceResponse> => {

      if (!data.projectExportContentId) {
        return Promise.reject(new Error("O ID do projeto de exportação é necessário"));
      }
      const API_BASE_URL = process.env.REACT_APP_BASE_URL;

      const url = `${API_BASE_URL}/routes/lurik-ai/inference`;

      try {
        const response = await axiosAuthInstance.post(url, data);
        return response.data;
      } catch (error) {
        throw error;
      }
    },
    [axiosAuthInstance]
  );



  return {
    projectIsExportingApi,
    findUserProjects,
    findProjectById,
    updateFilter,
    exportProjectApi,
    createProjectApi,
    updateProjectStatusApi,
    cloneProjectApi,
    updateProjectNameApi,
    fetchInference
  }
}